import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import DownCaretIcon from 'common/Icons/DownCaretIcon';
import { useUserSwitch } from 'components/layout/hooks';
import Button from 'components/lib/Button';
import Dropdown from 'components/lib/Dropdown';
import { useAppSelector } from 'store/hook';
import { CommunityAdminListType, CommunityType } from 'types/communities';

import CommunityAuditsModal from '../audits/CommunityAuditsModal';
import { useCommunitiesContext } from '../CommunitiesContext';
import CommunityAdminModal from '../create/CommunityAdminModal';

interface Props {
  item: CommunityAdminListType;
}

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ActionButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CommunitiesTableActions = ({ item }: Props) => {
  const { currentUser } = useAppSelector((state) => state.auth);

  const { refetch } = useCommunitiesContext();
  const [selectedCommunityId, setSelectedCommunityId] = useState<number | undefined>(undefined);
  const [auditsModalOpened, setAuditsModalOpened] = useState<boolean>(false);

  const { setManageContactsOpened, setManageIncentivesOpened, setSelectedCommunityInfo } = useCommunitiesContext();
  const { handleUserSwitch } = useUserSwitch();
  const navigate = useNavigate();

  const viewCommunity = () => {
    navigate(`/communities/${item.id}/info`);
  };

  const manageIncentives = () => {
    const communityType: CommunityType = {
      ...item,
      company: item.company,
      competitors: item.competitors.map((c) => c.id),
    };
    setSelectedCommunityInfo(communityType);
    setManageIncentivesOpened(true);
  };

  const manageContacts = () => {
    const communityType: CommunityType = {
      ...item,
      company: item.company,
      competitors: item.competitors.map((c) => c.id),
    };
    setSelectedCommunityInfo(communityType);
    setManageContactsOpened(true);
  };

  const onEnterSupportMode = () => {
    const primaryUserId = item.company?.primary_user?.id;
    if (primaryUserId) {
      handleUserSwitch(primaryUserId, () => {
        navigate(`/community/${item.id}/info`);
      });
    }
  };

  const supportModeEnabled = Boolean(currentUser?.isSuperuser && item.company?.primary_user?.id);
  const auditsEnabled = Boolean(currentUser?.isSuperuser || currentUser?.isStaff);

  const menuItems = [
    ...(currentUser?.isSuperuser
      ? [
          {
            label: 'View Community',
            key: 'view',
            onClick: viewCommunity,
          },
          {
            label: 'Edit Community',
            key: 'edit',
            onClick: () => {
              setSelectedCommunityId(item.id);
            },
          },
        ]
      : []),
    {
      label: 'Manage Contacts',
      key: 'contacts',
      onClick: manageContacts,
    },
    {
      label: 'Manage Incentives',
      key: 'incentive',
      onClick: manageIncentives,
    },
    ...(auditsEnabled
      ? [
          {
            label: 'View Audits',
            key: 'audits',
            onClick: () => setAuditsModalOpened(true),
          },
        ]
      : []),
    ...(supportModeEnabled
      ? [
          {
            label: 'Enter Support Mode',
            key: 'support',
            onClick: onEnterSupportMode,
          },
        ]
      : []),
  ];

  return (
    <ActionsContainer className="actions-hover-reveal">
      <Dropdown
        menu={{
          items: menuItems,
        }}
      >
        <Button style={{ width: 100 }}>
          <ActionButtonContent>
            <span>Actions</span>
            <DownCaretIcon />
          </ActionButtonContent>
        </Button>
      </Dropdown>
      {selectedCommunityId && (
        <CommunityAdminModal
          open={Boolean(selectedCommunityId)}
          communityId={selectedCommunityId}
          onClose={() => {
            refetch();
            setSelectedCommunityId(undefined);
          }}
        />
      )}
      {auditsModalOpened && (
        <CommunityAuditsModal
          openModal={auditsModalOpened}
          communityInfo={item}
          onClose={() => setAuditsModalOpened(false)}
        />
      )}
    </ActionsContainer>
  );
};

export default CommunitiesTableActions;
